import { useMemo } from 'react';

import type { CostWindow } from '../../../types/optimise';
import { buildRateShadingBlocksFromCostWindows } from '../../../utils/socChartUtils';

import { buildShadingPoints } from './socChartDerivations';

type ShadingBlock = Array<{ x: string; y: number; y0: number }>;

export type ShadingBlocks = {
  cheap: ShadingBlock[];
  peak: ShadingBlock[];
};

type ChartPoint = {
  x: string;
  y?: number;
  _ts?: number;
  [key: string]: unknown;
};

type Props = {
  behaviorChartData: ChartPoint[] | null | undefined;
  costWindows: CostWindow[] | null | undefined;
  costWindowsExport: CostWindow[] | null | undefined;
  tariffRateView: 'import' | 'export';
};

const EMPTY_BLOCKS: ShadingBlocks = { cheap: [], peak: [] };

export type ShadingBlocksAll = {
  active: ShadingBlocks;
  importBlocks: ShadingBlocks;
  exportBlocks: ShadingBlocks;
};

/**
 * Derives the active tariff shading blocks (cheap + peak) for the SOC chart.
 *
 * Shading is computed across the full combined chart axis (historic + future) using
 * the provided cost windows. Returns blocks for the currently selected `tariffRateView`.
 */
export const useTariffShadingBlocks = ({
  behaviorChartData,
  costWindows,
  costWindowsExport,
  tariffRateView,
}: Props): ShadingBlocksAll => {
  const shadingPoints = useMemo(() => buildShadingPoints(behaviorChartData), [behaviorChartData]);

  const importShadingBlocks: ShadingBlocks = useMemo(() => {
    const blocks = buildRateShadingBlocksFromCostWindows(shadingPoints, costWindows ?? []);
    return blocks;
  }, [shadingPoints, costWindows]);

  const exportShadingBlocksFull: ShadingBlocks = useMemo(() => {
    const blocks = buildRateShadingBlocksFromCostWindows(shadingPoints, costWindowsExport ?? []);
    return blocks;
  }, [shadingPoints, costWindowsExport]);

  const active = tariffRateView === 'export' ? exportShadingBlocksFull : importShadingBlocks;

  return {
    active: active ?? EMPTY_BLOCKS,
    importBlocks: importShadingBlocks,
    exportBlocks: exportShadingBlocksFull,
  };
};
